import { useEffect, useState } from "react";
import { Plus, PaperPlaneTilt, Money, XCircle, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import PrepayLedger from "../components/PrepayLedger";

const METHODS = ["check", "venmo", "zelle", "card", "cash", "other"];
const STATUS_CHIP = {
  draft: "bg-stone-100 text-stone-500 border-stone-300",
  sent: "bg-primary/10 text-primary border-primary/30",
  partial: "bg-[#B7791F]/10 text-[#8a5d14] border-[#B7791F]/40",
  paid: "bg-[#27AE60]/10 text-[#1e8449] border-[#27AE60]/40",
  void: "bg-stone-100 text-stone-400 border-stone-200 line-through",
};

function ClientBillingEditor({ client, onSaved, onOpenLedger }) {
  const b = client.billing || {};
  const [f, setF] = useState({ private_pay: !!b.private_pay, hourly_rate: b.hourly_rate || "", weekend_rate: b.weekend_rate || "", payer_name: b.payer_name || "", payer_email: b.payer_email || "",
    prepay_enabled: !!b.prepay_enabled, prepay_day: b.prepay_day ?? "", prepay_amount: b.prepay_amount || "", prepay_note: b.prepay_note || "" });
  const [open, setOpen] = useState(false);
  const save = async () => {
    try {
      const { data } = await api.put(`/clients/${client.id}/billing`, { ...f, hourly_rate: Number(f.hourly_rate) || 0, weekend_rate: Number(f.weekend_rate) || 0, prepay_amount: Number(f.prepay_amount) || 0 });
      toast.success(`Saved — balance on account: $${Math.abs(data.open_balance).toFixed(2)} ${data.open_balance > 0 ? "owed" : data.open_balance < 0 ? "credit" : ""}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="rounded-lg border border-stone-100 px-4 py-3" data-testid={`billing-client-${client.id}`}>
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between text-left">
        <span className="text-sm font-medium text-stone-800">{client.name}</span>
        <span className="text-xs text-stone-400">{f.private_pay ? `$${f.hourly_rate || "?"}/hr` : "not set up"}{f.prepay_enabled ? " · prepay" : ""} {open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid={`billing-editor-${client.id}`}>
          <label className="flex items-center gap-2 text-sm text-stone-700 sm:col-span-2">
            <input type="checkbox" checked={f.private_pay} onChange={(e) => setF({ ...f, private_pay: e.target.checked })} data-testid={`billing-private-${client.id}`} />
            Private-pay client (weekly running invoice)
          </label>
          <div className="space-y-1"><Label>Weekday $/hr</Label><Input type="number" step="0.25" value={f.hourly_rate} onChange={(e) => setF({ ...f, hourly_rate: e.target.value })} className="border-stone-300" data-testid={`billing-rate-${client.id}`} /></div>
          <div className="space-y-1"><Label>Weekend/holiday $/hr</Label><Input type="number" step="0.25" value={f.weekend_rate} onChange={(e) => setF({ ...f, weekend_rate: e.target.value })} className="border-stone-300" placeholder="same as weekday if blank" data-testid={`billing-wrate-${client.id}`} /></div>
          <div className="space-y-1"><Label>Payer / responsible party</Label><Input value={f.payer_name} onChange={(e) => setF({ ...f, payer_name: e.target.value })} className="border-stone-300" data-testid={`billing-payer-${client.id}`} /></div>
          <div className="space-y-1"><Label>Payer email</Label><Input type="email" value={f.payer_email} onChange={(e) => setF({ ...f, payer_email: e.target.value })} className="border-stone-300" data-testid={`billing-pemail-${client.id}`} /></div>
          <label className="flex items-center gap-2 text-sm text-stone-700 sm:col-span-2">
            <input type="checkbox" checked={f.prepay_enabled} onChange={(e) => setF({ ...f, prepay_enabled: e.target.checked })} data-testid={`billing-prepay-${client.id}`} />
            Paid in advance (prepay plan — payments cover upcoming services, credit/shortage rolls forward)
          </label>
          {f.prepay_enabled && (
            <>
              <div className="space-y-1"><Label>Prepay day</Label>
                <select value={f.prepay_day} onChange={(e) => setF({ ...f, prepay_day: e.target.value })} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid={`billing-prepay-day-${client.id}`}>
                  <option value="">—</option>
                  {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </div>
              <div className="space-y-1"><Label>Arranged prepay amount ($)</Label><Input type="number" step="0.01" value={f.prepay_amount} onChange={(e) => setF({ ...f, prepay_amount: e.target.value })} className="border-stone-300" data-testid={`billing-prepay-amount-${client.id}`} /></div>
              <div className="space-y-1 sm:col-span-2"><Label>Client note (shown on the payment history)</Label><Input value={f.prepay_note} onChange={(e) => setF({ ...f, prepay_note: e.target.value })} className="border-stone-300" data-testid={`billing-prepay-note-${client.id}`} /></div>
            </>
          )}
          <div className="flex items-center gap-3 sm:col-span-2">
            <Button size="sm" onClick={save} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`billing-save-${client.id}`}>Save billing setup</Button>
            {b.prepay_enabled && (
              <button onClick={() => onOpenLedger(client.id)} className="text-xs font-semibold text-primary hover:underline" data-testid={`billing-ledger-${client.id}`}>Open prepay ledger →</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AdjustmentForm({ inv, onDone }) {
  const [a, setA] = useState({ date: new Date().toISOString().slice(0, 10), kind: "credit", amount: "", reason: "" });
  const add = async () => {
    if (!Number(a.amount) || !a.reason.trim()) { toast.error("Amount and reason are required"); return; }
    try {
      const { data } = await api.post(`/billing/invoices/${inv.id}/adjustments`, { ...a, amount: Number(a.amount) });
      toast.success(`Adjustment applied — new total $${data.total_due.toFixed(2)}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-wrap items-end gap-1.5 rounded-lg border border-stone-200 bg-stone-50 p-2.5" data-testid={`invoice-adjform-${inv.id}`}>
      <span className="w-full text-[10px] font-bold uppercase tracking-wide text-stone-400">Add adjustment (credit or charge)</span>
      <Input type="date" value={a.date} onChange={(e) => setA({ ...a, date: e.target.value })} className="h-8 w-32 border-stone-300 bg-white text-xs" data-testid={`adj-date-${inv.id}`} />
      <select value={a.kind} onChange={(e) => setA({ ...a, kind: e.target.value })} className="h-8 rounded-md border border-stone-300 bg-white px-1.5 text-xs" data-testid={`adj-kind-${inv.id}`}>
        <option value="credit">credit (they pay less)</option><option value="charge">charge (they pay more)</option>
      </select>
      <Input type="number" step="0.01" placeholder="$" value={a.amount} onChange={(e) => setA({ ...a, amount: e.target.value })} className="h-8 w-24 border-stone-300 bg-white text-xs" data-testid={`adj-amount-${inv.id}`} />
      <Input placeholder="reason (e.g. caregiver called out — not billed)" value={a.reason} onChange={(e) => setA({ ...a, reason: e.target.value })} className="h-8 min-w-40 flex-1 border-stone-300 bg-white text-xs" data-testid={`adj-reason-${inv.id}`} />
      <Button size="sm" variant="outline" onClick={add} className="border-stone-300" data-testid={`adj-add-${inv.id}`}>Add</Button>
    </div>
  );
}

export default function BillingPage() {
  const [settings, setSettings] = useState(null);
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [addId, setAddId] = useState("");
  const [weekOf, setWeekOf] = useState(() => { const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1); return d.toISOString().slice(0, 10); });
  const [expanded, setExpanded] = useState(null);
  const [ledgerFor, setLedgerFor] = useState(null);
  const [pay, setPay] = useState({ amount: "", method: "check", date: new Date().toISOString().slice(0, 10), note: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: inv }, { data: st }] = await Promise.all([api.get("/billing/invoices"), api.get("/billing/settings")]);
      setData(inv); setClients(inv.private_pay_clients || []); setSettings(st);
    } catch (e) { toast.error(formatApiError(e)); }
    api.get("/clients").then((r) => setAllClients(Array.isArray(r.data) ? r.data : r.data.clients || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const saveSettings = async () => {
    try { await api.put("/billing/settings", settings); toast.success("Payment instructions saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/generate", { week_of: weekOf });
      toast.success(`${data.created} draft invoice(s) for week of ${data.week_of}${data.skipped.length ? ` — ${data.skipped.length} skipped` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const send = async (inv) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/billing/invoices/${inv.id}/send`);
      toast.success(`Sent to the payer${data.pay_url ? " with an online pay link" : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const recordPayment = async (inv) => {
    if (!Number(pay.amount)) { toast.error("Enter the amount received"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/billing/invoices/${inv.id}/payments`, { ...pay, amount: Number(pay.amount) });
      toast.success(data.overpayment_credit > 0 ? `Recorded — $${data.overpayment_credit.toFixed(2)} overpayment is now credit on account` : "Payment recorded");
      setPay({ amount: "", method: "check", date: new Date().toISOString().slice(0, 10), note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const voidInv = async (inv) => {
    if (!window.confirm(`Void the week-of-${inv.week_of} invoice for ${inv.client_name}?`)) return;
    try { await api.post(`/billing/invoices/${inv.id}/void`); toast.success("Voided"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const clientName = (c) => c.forms?.intake?.full_name || c.full_name || c.client_code;

  return (
    <div className="max-w-4xl" data-testid="billing-page">
      <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Billing</h1>
      <p className="mt-1 text-sm text-stone-500">
        Weekly running invoices for private-pay clients. Hours come straight from each client's schedule — the app prepares a draft
        every Monday morning, you glance and hit send. Shortages carry forward; overpayments become credit automatically.
      </p>

      {settings && (
        <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="billing-settings-card">
          <p className="font-heading text-sm font-semibold text-stone-900">How families can pay</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <div className="space-y-1"><Label>Venmo</Label><Input value={settings.venmo} onChange={(e) => setSettings({ ...settings, venmo: e.target.value })} placeholder="@YourVenmo" className="border-stone-300" data-testid="billing-venmo" /></div>
            <div className="space-y-1"><Label>Zelle</Label><Input value={settings.zelle} onChange={(e) => setSettings({ ...settings, zelle: e.target.value })} placeholder="email or phone" className="border-stone-300" data-testid="billing-zelle" /></div>
            <div className="space-y-1"><Label>Note on every invoice</Label><Input value={settings.invoice_note} onChange={(e) => setSettings({ ...settings, invoice_note: e.target.value })} placeholder="e.g. Thank you for trusting us with your family" className="border-stone-300" data-testid="billing-note" /></div>
          </div>
          <p className="mt-2 text-[11px] text-stone-400">Card payments happen through a secure online link on each invoice — no setup needed. Venmo/Zelle appear as instructions.</p>
          <Button size="sm" onClick={saveSettings} className="mt-3 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="billing-settings-save">Save</Button>
        </div>
      )}

      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="billing-clients-card">
        <p className="font-heading text-sm font-semibold text-stone-900">Private-pay clients ({clients.length})</p>
        <div className="mt-3 space-y-2">
          {clients.map((c) => <ClientBillingEditor key={c.id} client={c} onSaved={load} onOpenLedger={setLedgerFor} />)}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <select value={addId} onChange={(e) => setAddId(e.target.value)} className="h-9 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="billing-add-client">
            <option value="">+ set up a client for private pay…</option>
            {allClients.filter((c) => !clients.some((x) => x.id === c.id)).map((c) => <option key={c.id} value={c.id}>{clientName(c)}</option>)}
          </select>
          <Button size="sm" variant="outline" disabled={!addId} className="border-stone-300"
            onClick={async () => { try { await api.put(`/clients/${addId}/billing`, { private_pay: true, hourly_rate: 0 }); toast.success("Added — set the rate below"); setAddId(""); load(); } catch (e) { toast.error(formatApiError(e)); } }}
            data-testid="billing-add-button">Add</Button>
        </div>
      </div>

      {ledgerFor && <PrepayLedger clientId={ledgerFor} onClose={() => setLedgerFor(null)} />}

      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="billing-generate-card">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="font-heading text-sm font-semibold text-stone-900">Weekly drafts</p>
            <p className="mt-0.5 text-xs text-stone-500">Drafts auto-generate every Monday morning. You can also run it manually for any week.</p>
          </div>
          <div className="flex items-end gap-2">
            <div className="space-y-1"><Label>Week of</Label><Input type="date" value={weekOf} onChange={(e) => setWeekOf(e.target.value)} className="border-stone-300" data-testid="billing-week-input" /></div>
            <Button onClick={generate} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="billing-generate-button">
              <Plus size={14} className="mr-1" /> {busy ? "Working…" : "Generate drafts"}
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="billing-invoices-card">
        <p className="font-heading text-sm font-semibold text-stone-900">Invoices</p>
        <div className="mt-3 space-y-2" data-testid="billing-invoices-list">
          {(data?.invoices || []).length === 0 && <p className="text-xs text-stone-400" data-testid="billing-empty">No invoices yet — set up a private-pay client above, then generate drafts.</p>}
          {(data?.invoices || []).map((inv) => (
            <div key={inv.id} className="rounded-lg border border-stone-100 px-4 py-3" data-testid={`invoice-${inv.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <button onClick={() => setExpanded(expanded === inv.id ? null : inv.id)} className="min-w-0 flex-1 text-left" data-testid={`invoice-expand-${inv.id}`}>
                  <p className="text-sm font-medium text-stone-800">{inv.client_name} <span className="text-stone-400">· week of {inv.week_of}</span></p>
                  <p className="text-xs text-stone-500">
                    {inv.weekday_hours}h weekday + {inv.weekend_hours}h weekend = ${inv.subtotal?.toFixed(2)}
                    {inv.prior_shortage > 0 && ` · +$${inv.prior_shortage.toFixed(2)} carried`}
                    {inv.credit_applied > 0 && ` · −$${inv.credit_applied.toFixed(2)} credit`}
                    {" · "}<strong className="text-stone-700">due ${inv.total_due?.toFixed(2)}</strong>
                    {inv.paid_total > 0 && ` · paid $${inv.paid_total.toFixed(2)}`}
                  </p>
                </button>
                <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase ${STATUS_CHIP[inv.status]}`} data-testid={`invoice-status-${inv.id}`}>{inv.status}</span>
              </div>
              {expanded === inv.id && (
                <div className="mt-3 space-y-3 border-t border-stone-100 pt-3" data-testid={`invoice-detail-${inv.id}`}>
                  <div className="flex flex-wrap items-center gap-2 text-[11px]">
                    <span className={`rounded-full px-2 py-0.5 font-bold ${inv.source === "evv" ? "bg-[#27AE60]/10 text-[#1e8449]" : "bg-stone-100 text-stone-500"}`} data-testid={`invoice-source-${inv.id}`}>
                      {inv.source === "evv" ? "Reconciled to actual visits (EVV)" : "Estimated from the schedule"}
                    </span>
                    {inv.source === "evv" && inv.variance != null && (
                      <span className="text-stone-500">estimate was ${inv.estimate_subtotal?.toFixed(2)} · variance {inv.variance >= 0 ? "+" : ""}${inv.variance?.toFixed(2)}</span>
                    )}
                    {inv.source !== "evv" && inv.status !== "void" && (
                      <button onClick={async () => { try { const { data } = await api.post(`/billing/invoices/${inv.id}/reconcile`); toast.success(`Reconciled to actual visits — variance ${data.variance >= 0 ? "+" : ""}$${data.variance.toFixed(2)}`); load(); } catch (e) { toast.error(formatApiError(e)); } }}
                        className="rounded-full border border-primary/40 px-2.5 py-0.5 font-medium text-primary transition-colors hover:bg-primary hover:text-white" data-testid={`invoice-reconcile-${inv.id}`}>
                        Reconcile to actual visits
                      </button>
                    )}
                  </div>
                  {(inv.lines || []).length > 0 && (
                    <div className="overflow-x-auto rounded-lg border border-stone-100" data-testid={`invoice-lines-${inv.id}`}>
                      <table className="w-full text-xs">
                        <thead><tr className="bg-stone-50 text-left text-stone-400"><th className="px-2 py-1.5">Date</th><th className="px-2 py-1.5">Caregiver</th><th className="px-2 py-1.5">In–Out</th><th className="px-2 py-1.5 text-right">Hours</th><th className="px-2 py-1.5 text-right">Charge</th></tr></thead>
                        <tbody>
                          {inv.lines.map((l, li) => (
                            <tr key={li} className="border-t border-stone-100" data-testid={`invoice-line-${inv.id}-${li}`}>
                              <td className="px-2 py-1">{l.date}</td>
                              <td className="px-2 py-1">{l.caregiver || "—"}</td>
                              <td className="px-2 py-1">{l.time_in && l.time_out ? `${l.time_in}–${l.time_out}` : ""}</td>
                              <td className="px-2 py-1 text-right">{l.hours}</td>
                              <td className="px-2 py-1 text-right">${l.charge?.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {(inv.adjustments || []).length > 0 && (
                    <div className="text-xs" data-testid={`invoice-adjs-${inv.id}`}>
                      <p className="font-semibold text-stone-600">Adjustments</p>
                      {inv.adjustments.map((a) => <p key={a.id} className="text-stone-500">{a.date || (a.at || "").slice(0, 10)} — {a.kind === "credit" ? "−" : "+"}${a.amount?.toFixed(2)} {a.kind}: {a.reason}</p>)}
                    </div>
                  )}
                  {inv.status !== "void" && inv.status !== "paid" && <AdjustmentForm inv={inv} onDone={load} />}
                  {inv.stripe_url && inv.status !== "paid" && inv.status !== "void" && (
                    <p className="truncate text-xs text-stone-500">Online pay link: <a href={inv.stripe_url} target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline" data-testid={`invoice-paylink-${inv.id}`}>{inv.stripe_url}</a></p>
                  )}
                  {(inv.payments || []).length > 0 && (
                    <div className="text-xs text-stone-500">
                      {(inv.payments || []).map((p) => <p key={p.id}>{p.date} — ${p.amount?.toFixed(2)} via {p.method}{p.note ? ` (${p.note})` : ""}</p>)}
                    </div>
                  )}
                  {inv.status !== "void" && inv.status !== "paid" && (
                    <div className="flex flex-wrap items-end gap-2">
                      {inv.status === "draft" && <Button size="sm" onClick={() => send(inv)} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`invoice-send-${inv.id}`}><PaperPlaneTilt size={13} className="mr-1" /> Send to payer</Button>}
                      {inv.status !== "draft" && <Button size="sm" variant="outline" onClick={() => send(inv)} disabled={busy} className="border-stone-300" data-testid={`invoice-resend-${inv.id}`}>Resend</Button>}
                      <div className="flex items-end gap-1.5">
                        <Input type="number" step="0.01" placeholder="$ amount" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="h-8 w-28 border-stone-300 text-xs" data-testid={`invoice-payamount-${inv.id}`} />
                        <select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} className="h-8 rounded-md border border-stone-300 bg-white px-1.5 text-xs" data-testid={`invoice-paymethod-${inv.id}`}>
                          {METHODS.map((m) => <option key={m}>{m}</option>)}
                        </select>
                        <Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} className="h-8 border-stone-300 text-xs" data-testid={`invoice-paydate-${inv.id}`} />
                        <Button size="sm" variant="outline" onClick={() => recordPayment(inv)} disabled={busy} className="border-stone-300" data-testid={`invoice-record-${inv.id}`}><Money size={13} className="mr-1" /> Record payment</Button>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => voidInv(inv)} className="text-stone-400 hover:text-[#C0392B]" data-testid={`invoice-void-${inv.id}`}><XCircle size={13} /></Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
